import { apiClient } from './api-client';

// ==================== Enums ====================

// Must match TriggerType enum from centraOnePlus-shared/prisma/schema.prisma
export enum N8nTriggerType {
  TICKET_CREATED = 'TICKET_CREATED',
  TICKET_UPDATED = 'TICKET_UPDATED',
  TICKET_STATUS_CHANGED = 'TICKET_STATUS_CHANGED',
  TICKET_ASSIGNED = 'TICKET_ASSIGNED',
  TICKET_PRIORITY_CHANGED = 'TICKET_PRIORITY_CHANGED',
  TICKET_TAG_ADDED = 'TICKET_TAG_ADDED',
  STAGE_ENTERED = 'STAGE_ENTERED',
  STAGE_EXITED = 'STAGE_EXITED',
  CATEGORY_CHANGED = 'CATEGORY_CHANGED',
  FIELD_VALUE_CHANGED = 'FIELD_VALUE_CHANGED',
  MESSAGE_RECEIVED = 'MESSAGE_RECEIVED',
  MESSAGE_SENT = 'MESSAGE_SENT',
  CHAT_STARTED = 'CHAT_STARTED',
  CHAT_ENDED = 'CHAT_ENDED',
  CHAT_AGENT_ASSIGNED = 'CHAT_AGENT_ASSIGNED',
  CUSTOMER_CREATED = 'CUSTOMER_CREATED',
  CUSTOMER_UPDATED = 'CUSTOMER_UPDATED',
  SLA_BREACH_WARNING = 'SLA_BREACH_WARNING',
  SLA_BREACHED = 'SLA_BREACHED',
  TIME_BASED = 'TIME_BASED',
  WEBHOOK_RECEIVED = 'WEBHOOK_RECEIVED',
}

export enum N8nExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

// ==================== Interfaces ====================

export interface N8nWorkflow {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  isActive: boolean;
  n8nWorkflowId: string;
  webhookUrl?: string;
  triggerType?: N8nTriggerType;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface N8nExecution {
  id: string;
  tenantId: string;
  workflowId: string;
  n8nExecutionId?: string;
  triggerPayload: Record<string, unknown>;
  entityType?: string;
  entityId?: string;
  status: N8nExecutionStatus;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  result?: Record<string, unknown>;
  createdAt: string;
}

export interface N8nAvailableWorkflow {
  id: string;
  name: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  tags?: string[];
}

export interface CreateN8nWorkflowDto {
  name: string;
  description?: string;
  n8nWorkflowId: string;
  webhookUrl?: string;
  triggerType?: N8nTriggerType;
  isActive?: boolean;
}

export interface UpdateN8nWorkflowDto {
  name?: string;
  description?: string;
  n8nWorkflowId?: string;
  webhookUrl?: string;
  triggerType?: N8nTriggerType;
  isActive?: boolean;
}

export interface N8nWorkflowFilter {
  triggerType?: N8nTriggerType;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface N8nExecutionFilter {
  workflowId?: string;
  status?: N8nExecutionStatus;
  page?: number;
  limit?: number;
}

export interface PaginatedN8nWorkflowsResponse {
  data: N8nWorkflow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedN8nExecutionsResponse {
  data: N8nExecution[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface N8nTriggerResult {
  success: boolean;
  executionId?: string;
  n8nExecutionId?: string;
  error?: string;
}

export interface N8nConnectionTestResult {
  success: boolean;
  message: string;
  workflowCount?: number;
}

// ==================== Service ====================

class N8nService {
  private readonly basePath = '/workflows/n8n';

  // ==================== Workflow Management ====================

  async createWorkflow(dto: CreateN8nWorkflowDto): Promise<N8nWorkflow> {
    const response = await apiClient.post<N8nWorkflow>(`${this.basePath}/workflows`, dto);
    return response.data;
  }

  async getWorkflows(filter: N8nWorkflowFilter = {}): Promise<PaginatedN8nWorkflowsResponse> {
    const params = new URLSearchParams();
    if (filter.triggerType) params.append('triggerType', filter.triggerType);
    if (filter.isActive !== undefined) params.append('isActive', String(filter.isActive));
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));

    const response = await apiClient.get<PaginatedN8nWorkflowsResponse>(
      `${this.basePath}/workflows?${params.toString()}`
    );
    return response.data;
  }

  async getWorkflow(workflowId: string): Promise<N8nWorkflow> {
    const response = await apiClient.get<N8nWorkflow>(`${this.basePath}/workflows/${workflowId}`);
    return response.data;
  }

  async updateWorkflow(workflowId: string, dto: UpdateN8nWorkflowDto): Promise<N8nWorkflow> {
    const response = await apiClient.put<N8nWorkflow>(
      `${this.basePath}/workflows/${workflowId}`,
      dto
    );
    return response.data;
  }

  async deleteWorkflow(workflowId: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/workflows/${workflowId}`);
  }

  async toggleWorkflow(workflowId: string, isActive: boolean): Promise<N8nWorkflow> {
    const response = await apiClient.patch<N8nWorkflow>(
      `${this.basePath}/workflows/${workflowId}/toggle`,
      { isActive }
    );
    return response.data;
  }

  // ==================== Workflow Triggering ====================

  async triggerWorkflow(
    workflowId: string,
    payload: Record<string, unknown> = {}
  ): Promise<N8nTriggerResult> {
    // Backend expects { payload: {...} } structure (TriggerN8nWorkflowDto)
    const response = await apiClient.post<N8nTriggerResult>(
      `${this.basePath}/workflows/${workflowId}/trigger`,
      { payload }
    );
    return response.data;
  }

  // ==================== Executions ====================

  async getExecutions(filter: N8nExecutionFilter = {}): Promise<PaginatedN8nExecutionsResponse> {
    const params = new URLSearchParams();
    if (filter.workflowId) params.append('workflowId', filter.workflowId);
    if (filter.status) params.append('status', filter.status);
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));

    const response = await apiClient.get<PaginatedN8nExecutionsResponse>(
      `${this.basePath}/executions?${params.toString()}`
    );
    return response.data;
  }

  async getExecution(executionId: string): Promise<N8nExecution> {
    const response = await apiClient.get<N8nExecution>(
      `${this.basePath}/executions/${executionId}`
    );
    return response.data;
  }

  // ==================== n8n Instance Integration ====================

  async getAvailableWorkflows(): Promise<N8nAvailableWorkflow[]> {
    const response = await apiClient.get<N8nAvailableWorkflow[]>(
      `${this.basePath}/available-workflows`
    );
    return response.data;
  }

  async getAvailableWorkflowDetails(n8nWorkflowId: string): Promise<unknown> {
    const response = await apiClient.get<unknown>(
      `${this.basePath}/available-workflows/${n8nWorkflowId}`
    );
    return response.data;
  }

  async testConnection(): Promise<N8nConnectionTestResult> {
    try {
      // Test connection by fetching available workflows
      const workflows = await this.getAvailableWorkflows();
      return {
        success: true,
        message: `Connected successfully`,
        workflowCount: workflows.length,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || 'Failed to connect to n8n',
      };
    }
  }

  // ==================== Helpers ====================

  getTriggerTypeLabel(type: N8nTriggerType): string {
    const labels: Record<N8nTriggerType, string> = {
      [N8nTriggerType.TICKET_CREATED]: 'Ticket Created',
      [N8nTriggerType.TICKET_UPDATED]: 'Ticket Updated',
      [N8nTriggerType.TICKET_STATUS_CHANGED]: 'Ticket Status Changed',
      [N8nTriggerType.TICKET_ASSIGNED]: 'Ticket Assigned',
      [N8nTriggerType.TICKET_PRIORITY_CHANGED]: 'Priority Changed',
      [N8nTriggerType.TICKET_TAG_ADDED]: 'Tag Added',
      [N8nTriggerType.STAGE_ENTERED]: 'Stage Entered',
      [N8nTriggerType.STAGE_EXITED]: 'Stage Exited',
      [N8nTriggerType.CATEGORY_CHANGED]: 'Category Changed',
      [N8nTriggerType.FIELD_VALUE_CHANGED]: 'Field Value Changed',
      [N8nTriggerType.MESSAGE_RECEIVED]: 'Message Received',
      [N8nTriggerType.MESSAGE_SENT]: 'Message Sent',
      [N8nTriggerType.CHAT_STARTED]: 'Chat Started',
      [N8nTriggerType.CHAT_ENDED]: 'Chat Ended',
      [N8nTriggerType.CHAT_AGENT_ASSIGNED]: 'Chat Agent Assigned',
      [N8nTriggerType.CUSTOMER_CREATED]: 'Customer Created',
      [N8nTriggerType.CUSTOMER_UPDATED]: 'Customer Updated',
      [N8nTriggerType.SLA_BREACH_WARNING]: 'SLA Breach Warning',
      [N8nTriggerType.SLA_BREACHED]: 'SLA Breached',
      [N8nTriggerType.TIME_BASED]: 'Time Based (Scheduled)',
      [N8nTriggerType.WEBHOOK_RECEIVED]: 'Webhook Received',
    };
    return labels[type] || type;
  }

  getExecutionStatusColor(status: N8nExecutionStatus): string {
    const colors: Record<N8nExecutionStatus, string> = {
      [N8nExecutionStatus.PENDING]: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      [N8nExecutionStatus.RUNNING]: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      [N8nExecutionStatus.COMPLETED]:
        'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      [N8nExecutionStatus.FAILED]: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
      [N8nExecutionStatus.SKIPPED]:
        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  }
}

export const n8nService = new N8nService();
export default n8nService;
